import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyTuringMode({ brand }: ModeRendererProps) {
  // Abstract mind; auditably logical systems
  // State machines, tapes, decision trees
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Turing machine tape */}
      {Array.from({ length: 16 }).map((_, i) => {
        const x = 60 + i * 43;
        const y = 120;
        const symbols = ["0", "1", "⊔", "a", "b"];
        const symbol = symbols[i % 5];
        const isActive = i === 7;
        
        return (
          <g key={`tape-${i}`}>
            <rect
              x={x}
              y={y}
              width={40}
              height={60}
              fill={isActive ? brand.aqua : brand.white}
              stroke={brand.deep}
              strokeWidth="2"
              opacity={isActive ? 0.7 : 0.4}
            />
            <text
              x={x + 20}
              y={y + 38}
              textAnchor="middle"
              fontFamily="monospace"
              fontSize="20"
              fill={brand.deep}
              opacity={0.9}
            >
              {symbol}
            </text>
          </g>
        );
      })}
      
      {/* Read/write head */}
      <polygon
        points="360,200 340,220 380,220"
        fill={brand.violet}
        opacity={0.7}
      />
      <rect x="350" y="220" width="20" height="40" fill={brand.violet} opacity={0.7} />
      
      {/* State machine diagram */}
      {[
        { id: "q0", x: 150, y: 350, label: "START" },
        { id: "q1", x: 350, y: 300, label: "READ" },
        { id: "q2", x: 550, y: 350, label: "WRITE" },
        { id: "q3", x: 350, y: 420, label: "HALT" },
      ].map((state, i) => (
        <g key={`state-${i}`}>
          <circle
            cx={state.x}
            cy={state.y}
            r="45"
            fill={state.id === "q1" ? brand.aqua : brand.white}
            stroke={brand.deep}
            strokeWidth="3"
            opacity={0.7}
          />
          <text
            x={state.x}
            y={state.y - 5}
            textAnchor="middle"
            fontFamily="monospace"
            fontSize="14"
            fill={brand.deep}
            fontWeight="bold"
          >
            {state.id}
          </text>
          <text
            x={state.x}
            y={state.y + 12}
            textAnchor="middle"
            fontFamily="sans-serif"
            fontSize="10"
            fill={brand.deep}
            opacity={0.7}
          >
            {state.label}
          </text>
        </g>
      ))}
      
      {/* State transitions */}
      {[
        { from: { x: 195, y: 340 }, to: { x: 305, y: 315 }, label: "0→0,R" },
        { from: { x: 395, y: 315 }, to: { x: 505, y: 340 }, label: "1→1,R" },
        { from: { x: 540, y: 395 }, to: { x: 360, y: 420 }, label: "⊔→⊔,H" },
        { from: { x: 340, y: 375 }, to: { x: 340, y: 345 }, label: "a→b,L" },
      ].map((trans, i) => (
        <g key={`transition-${i}`}>
          <defs>
            <marker
              id={`arrow-${i}`}
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill={brand.violet} />
            </marker>
          </defs>
          <path
            d={`M ${trans.from.x},${trans.from.y} Q ${(trans.from.x + trans.to.x) / 2},${(trans.from.y + trans.to.y) / 2 - 30} ${trans.to.x},${trans.to.y}`}
            fill="none"
            stroke={brand.violet}
            strokeWidth="2"
            markerEnd={`url(#arrow-${i})`}
            opacity={0.6}
          />
          <text
            x={(trans.from.x + trans.to.x) / 2}
            y={(trans.from.y + trans.to.y) / 2 - 35}
            textAnchor="middle"
            fontFamily="monospace"
            fontSize="11"
            fill={brand.violet}
            opacity={0.8}
          >
            {trans.label}
          </text>
        </g>
      ))}
      
      {/* Decision tree - binary logic */}
      {Array.from({ length: 4 }).map((_, level) =>
        Array.from({ length: Math.pow(2, level) }).map((_, node) => {
          const x = 397 - (Math.pow(2, 3 - level) - 1) * 50 + node * Math.pow(2, 4 - level) * 50;
          const y = 540 + level * 80;
          const labels = ["Root", "0", "1"];
          
          return (
            <g key={`tree-${level}-${node}`}>
              <circle
                cx={x}
                cy={y}
                r="25"
                fill={level === 0 ? brand.aqua : brand.white}
                stroke={brand.deep}
                strokeWidth="2"
                opacity={0.6}
              />
              {level < 3 && (
                <>
                  <line
                    x1={x}
                    y1={y + 25}
                    x2={x - Math.pow(2, 3 - level) * 25}
                    y2={y + 80 - 25}
                    stroke={brand.deep}
                    strokeWidth="1.5"
                    opacity={0.4}
                  />
                  <line
                    x1={x}
                    y1={y + 25}
                    x2={x + Math.pow(2, 3 - level) * 25}
                    y2={y + 80 - 25}
                    stroke={brand.deep}
                    strokeWidth="1.5"
                    opacity={0.4}
                  />
                </>
              )}
              {level === 0 && (
                <text
                  x={x}
                  y={y + 5}
                  textAnchor="middle"
                  fontFamily="monospace"
                  fontSize="10"
                  fill={brand.deep}
                >
                  ROOT
                </text>
              )}
            </g>
          );
        })
      )}
      
      {/* Vasarely optical grid - logic gates */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 12 }).map((_, col) => {
          const x = 80 + col * 54;
          const y = 900 + row * 26;
          const gate = (row * 12 + col) % 4;
          const symbols = ["∧", "∨", "¬", "⊕"];
          
          return (
            <g key={`gate-${row}-${col}`}>
              <rect
                x={x}
                y={y}
                width={50}
                height={22}
                fill={gate === 0 ? brand.aqua : gate === 1 ? brand.violet : gate === 2 ? brand.amber : brand.white}
                stroke={brand.deep}
                strokeWidth="1"
                opacity={0.4}
              />
              <text
                x={x + 25}
                y={y + 16}
                textAnchor="middle"
                fontFamily="monospace"
                fontSize="14"
                fill={brand.deep}
                opacity={0.7}
              >
                {symbols[gate]}
              </text>
            </g>
          );
        })
      )}
      
      {/* Computational trace */}
      <text x="80" y="100" fontFamily="monospace" fontSize="12" fill={brand.deep} opacity={0.6}>
        COMPUTATIONAL TRACE: t = 0 → t = n
      </text>
    </g>
  );
}
